import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Title
} from "@mantine/core";
import type { ReactNode } from "react";
import { ModalFooter } from "../../../../../../mantine/components/ModalFooter/ModalFooter";
import { AlertPresets } from "../../../../../../mantine/mantinePresets";
import {
  VermarkterVollmachtUploadSuccessStatus,
  type components
} from "../../../../../../types/api.types";
import type { VermarkterVollmachtUploadSuccess } from "../hooks/useUploadVollmachtVermarkter";
import { formatAdditionalErrorMessage } from "./utils/formatAdditionalErrorMessage";
import {
  COMPANY_FIELDS,
  GENERATOR_FIELDS,
  STATUS_BADGE_BASE_COLORS,
  STATUS_FIELDS
} from "./VollmachtVermarkterConfirmation.constants";

const UNKNOWN_STATUS_LABEL = "Unbekannt";
const NOT_EXTRACTED_LABEL = "Nicht extrahiert";

type ValidationError = components["schemas"]["ValidationErrorCodeMessage"];

type FieldDefinition<TKey extends string> = {
  key: TKey;
  label: string;
  errorCode: string;
};

export interface VollmachtVermarkterConfirmationProps {
  isOpen: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onConfirmDataCorrect: () => void;
  onDiscardFile: () => void;
  uploadResponse: VermarkterVollmachtUploadSuccess;
}

function getStatusBadgeColor(
  status: string | undefined,
  errors: Array<ValidationError> | undefined
): "green" | "warning" | "error" {
  const baseColor = status ? STATUS_BADGE_BASE_COLORS[status] : undefined;
  if (errors?.some((error) => error.is_reason_for_rejection)) {
    return "error";
  }
  return baseColor || "error";
}

function getValidationErrorBadgeColor(
  error: ValidationError | undefined
): "warning" | "error" | undefined {
  if (!error) {
    return undefined;
  }

  return error.is_reason_for_rejection ? "error" : "warning";
}

function renderFieldRows<TKey extends string>({
  fields,
  values,
  errors
}: {
  fields: Array<FieldDefinition<TKey>>;
  values: Partial<Record<TKey, string>>;
  errors: Array<ValidationError> | undefined;
}) {
  return fields.map(({ key, label, errorCode }) => {
    const validationError = errors?.find((error) => error.code === errorCode);

    return (
      <FieldValueRow
        errorColor={getValidationErrorBadgeColor(validationError)}
        key={key}
        label={label}
        value={values[key]}
      />
    );
  });
}

function FieldValueRow({
  label,
  value,
  errorColor,
  errorLabel = "Nicht gefunden"
}: {
  label: string;
  value?: string;
  errorColor?: "warning" | "error";
  errorLabel?: string;
}) {
  return (
    <Group align="flex-start" justify="space-between" p="sm" wrap="nowrap">
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text fw={700} fz="sm" mb={2} span>
          {label}
        </Text>
        <Text fz="sm">{value ?? NOT_EXTRACTED_LABEL}</Text>
      </Box>
      {errorColor && (
        <Badge color={errorColor} radius="sm" variant="light">
          {errorLabel}
        </Badge>
      )}
    </Group>
  );
}

function SectionBlock({
  title,
  children
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Box pt="md" style={{ flex: 1, minWidth: 0 }}>
      <Title fw={600} fz="md" order={3}>
        {title}
      </Title>
      {children}
    </Box>
  );
}

function VollmachtVermarkterConfirmation({
  isOpen,
  isSubmitting,
  onClose,
  onConfirmDataCorrect,
  onDiscardFile,
  uploadResponse
}: VollmachtVermarkterConfirmationProps) {
  const company = uploadResponse.extracted_person ?? {};
  const generators = uploadResponse.extracted_generators ?? [];
  const additionalErrors = uploadResponse.additional_errors ?? [];

  const companyFieldValues = {
    name: company.name,
    street_and_nr: company.street_and_nr,
    postal_code_city: company.postal_code_city,
    abr_number: company.abr_number
  };

  const isRejected =
    uploadResponse.status === VermarkterVollmachtUploadSuccessStatus.rejected;
  const needsConfirmation =
    uploadResponse.status ===
    VermarkterVollmachtUploadSuccessStatus.need_confirmation;

  return (
    <Modal
      className="VollmachtVermarkterConfirmation"
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
      opened={isOpen}
      size="xl"
      title="Vollmacht für Vermarkter prüfen"
      withCloseButton={!isSubmitting}
      onClose={onClose}
    >
      <Stack gap="md">
        {needsConfirmation && (
          <Alert
            color={AlertPresets.info.color}
            icon={AlertPresets.info.icon}
            title="Bitte prüfen"
          >
            Die Vollmacht konnte erfolgreich verarbeitet werden, benötigt aber
            noch Ihre Bestätigung. Bitte prüfen Sie die extrahierten Daten auf
            Korrektheit.
          </Alert>
        )}

        {isRejected && (
          <Alert
            color={AlertPresets.error.color}
            icon={AlertPresets.error.icon}
            title="Bitte prüfen"
          >
            Die Vollmacht konnte nicht verarbeitet werden. Bitte prüfen Sie die
            extrahierten Daten auf Korrektheit und laden Sie die Vollmacht
            erneut hoch.
          </Alert>
        )}

        {additionalErrors.length > 0 && (
          <Alert
            color={AlertPresets.warning.color}
            icon={AlertPresets.warning.icon}
          >
            <Stack gap="xs">
              {additionalErrors.map((error, idx) => (
                <Group
                  align="flex-start"
                  justify="space-between"
                  key={`${error.code}-${error.message}-${idx}`}
                  wrap="nowrap"
                >
                  <Text fz="sm" style={{ flex: 1, minWidth: 0 }}>
                    {formatAdditionalErrorMessage(error)}
                    {error.is_reason_for_rejection}
                  </Text>
                  {error.is_reason_for_rejection && (
                    <Badge color={"error"} radius="sm" variant="light">
                      Ablehnungsgrund
                    </Badge>
                  )}
                </Group>
              ))}
            </Stack>
          </Alert>
        )}

        <SectionBlock title="Dokument">
          <Stack gap="xs" p="sm">
            {STATUS_FIELDS.map(({ label, statusKey, labelMap }) => {
              const statusDetails = uploadResponse[statusKey];
              const status = statusDetails?.status;
              const errors = statusDetails?.errors;
              return (
                <Group
                  gap="sm"
                  justify="space-between"
                  key={label}
                  wrap="nowrap"
                >
                  <Text fw={600} fz="sm">
                    {label}
                  </Text>
                  <Badge
                    color={getStatusBadgeColor(status, errors)}
                    radius="sm"
                    variant="light"
                  >
                    {status
                      ? (labelMap[status] ?? UNKNOWN_STATUS_LABEL)
                      : UNKNOWN_STATUS_LABEL}
                  </Badge>
                </Group>
              );
            })}
          </Stack>
        </SectionBlock>

        <SectionBlock title="Unternehmensdaten">
          <Stack gap="xs">
            {renderFieldRows({
              errors: company.errors,
              fields: COMPANY_FIELDS,
              values: companyFieldValues
            })}
          </Stack>
        </SectionBlock>

        <SectionBlock title="Erzeugungsanlagen">
          <Stack gap="sm">
            {generators.length === 0 && (
              <Text fz="sm">Keine Erzeugungsanlagen extrahiert.</Text>
            )}
            {generators.map((gen) => (
              <Stack gap="xs" key={gen.tr_id}>
                {renderFieldRows({
                  errors: gen.errors,
                  fields: GENERATOR_FIELDS,
                  values: { tr_id: gen.tr_id }
                })}
              </Stack>
            ))}
          </Stack>
        </SectionBlock>
      </Stack>
      <ModalFooter>
        <Button
          disabled={isSubmitting}
          variant="outline"
          onClick={onDiscardFile}
        >
          Daten verwerfen
        </Button>
        <Button
          disabled={isRejected}
          loading={isSubmitting}
          onClick={onConfirmDataCorrect}
        >
          Daten übernehmen
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export { VollmachtVermarkterConfirmation };
